import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import {
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Mail,
  ChevronLeft,
  CalendarClock,
  Send,
  Search,
  User,
  Copy,
  Eye,
  BarChart3,
  TrendingUp,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api-fetch';
import type {
  RecipientEmailHistoryEntry,
  CampaignEngagementStats,
  EmailCampaignRecipient,
} from '../types/bulk-email-campaigns';
import type { useEmailCampaignHistory } from '../hooks/useEmailCampaignHistory';
import {
  bouncedRecipientEmails,
  campaignListStatus,
  emailsToCsv,
  mergeRecipientEngagement,
  recipientEngagementFilterCounts,
  recipientMatchesEngagementFilter,
  recipientWasBounced,
  recipientWasOpened,
  type RecipientEngagementFilter,
} from '../utils/campaign-engagement';

type HistoryState = ReturnType<typeof useEmailCampaignHistory>;

interface EmailCampaignHistoryPanelProps extends HistoryState {}

function copyToClipboard(text: string) {
  void navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
}

function StatusPill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'opened' | 'delivered' | 'bounced' | 'muted';
}) {
  const tones = {
    opened: 'border-0 bg-primary/10 text-primary',
    delivered: 'border-0 bg-success/10 text-success',
    bounced: 'border-0 bg-destructive/10 text-destructive',
    muted: 'border-0 bg-muted text-muted-foreground',
  };

  return (
    <Badge variant="outline" className={tones[tone]}>
      {children}
    </Badge>
  );
}

const LIST_STATUS_TONE = {
  opened: 'opened',
  delivered: 'delivered',
  bounced: 'bounced',
  failed: 'bounced',
} as const;

const LIST_STATUS_LABEL = {
  opened: 'Opened',
  delivered: 'Delivered',
  bounced: 'Bounced',
  failed: 'Failed',
} as const;

function EngagementStatusBadge({ send }: { send: RecipientEmailHistoryEntry }) {
  if (!send.success) {
    return <StatusPill tone="muted">—</StatusPill>;
  }

  if (send.engagement_lookup_error) {
    return (
      <span className="text-xs text-muted-foreground" title={send.engagement_lookup_error}>
        Unavailable
      </span>
    );
  }

  if (recipientWasBounced(send)) {
    if (send.last_event === 'complained') {
      return <StatusPill tone="bounced">Marked spam</StatusPill>;
    }
    return <StatusPill tone="bounced">Bounced</StatusPill>;
  }

  if (send.last_event === 'clicked') {
    return <StatusPill tone="opened">Clicked link</StatusPill>;
  }

  if (recipientWasOpened(send)) {
    return <StatusPill tone="opened">Opened</StatusPill>;
  }

  return <StatusPill tone="delivered">Not opened</StatusPill>;
}

function CampaignEngagementPanel({
  campaignId,
  onEngagementLoaded,
}: {
  campaignId: string;
  onEngagementLoaded?: (recipients: EmailCampaignRecipient[]) => void;
}) {
  const [stats, setStats] = useState<CampaignEngagementStats | null>(null);
  const [enrichedRecipients, setEnrichedRecipients] = useState<EmailCampaignRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchEngagement = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/bulk-email/campaigns/${campaignId}/engagement?limit=2000`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats as CampaignEngagementStats);
        const loadedRecipients = (data.recipients as EmailCampaignRecipient[]) || [];
        setEnrichedRecipients(loadedRecipients);
        onEngagementLoaded?.(loadedRecipients);
        setLoaded(true);
      }
    } catch {
      toast.error('Could not load engagement data');
    } finally {
      setLoading(false);
    }
  }, [campaignId, onEngagementLoaded]);

  useEffect(() => {
    setLoaded(false);
    setStats(null);
    setEnrichedRecipients([]);
    onEngagementLoaded?.([]);
  }, [campaignId, onEngagementLoaded]);

  if (!loaded) {
    return (
      <div className="rounded-xl bg-muted/50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Open & bounce tracking
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Check which recipients opened the email, bounced, or clicked a link.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchEngagement} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <TrendingUp className="h-4 w-4 mr-2" />
            )}
            {loading ? 'Checking…' : 'Check engagement'}
          </Button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const total = stats.delivered + stats.opened + stats.clicked + stats.bounced + stats.complained;

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-muted/50 p-4 space-y-4">
        <p className="text-sm font-medium flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Engagement summary
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchEngagement}
            disabled={loading}
            className="ml-auto h-7"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </p>

        {total === 0 ? (
          <p className="text-sm text-muted-foreground">
            No tracking data available yet. Open tracking requires Resend with tracking enabled.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <StatCard label="Delivered" value={stats.delivered} className="text-primary" />
              <StatCard label="Opened" value={stats.opened} className="text-success" />
              <StatCard label="Clicked" value={stats.clicked} className="text-success" />
              <StatCard label="Bounced" value={stats.bounced} className="text-destructive" />
              <StatCard label="Spam" value={stats.complained} className="text-destructive" />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Open rate</span>
                  <span className="font-medium text-success">{stats.openRate}%</span>
                </div>
                <Progress value={stats.openRate} className="h-2" />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Bounce rate</span>
                  <span
                    className={`font-medium ${stats.bounceRate > 5 ? 'text-destructive' : 'text-muted-foreground'}`}
                  >
                    {stats.bounceRate}%
                  </span>
                </div>
                <Progress value={stats.bounceRate} className="h-2" />
              </div>
            </div>
          </>
        )}
      </div>

      {enrichedRecipients.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">Recipient engagement detail</p>
          <div className="table-scroll max-h-64">
            <table className="min-w-full border-separate border-spacing-y-2 text-sm">
              <thead>
                <tr className="text-xs font-medium text-muted-foreground">
                  <th className="px-4 pb-1 text-left">To</th>
                  <th className="px-4 pb-1 text-left">Status</th>
                  <th className="px-4 pb-1 text-left">Email ID</th>
                </tr>
              </thead>
              <tbody>
                {enrichedRecipients.map((r) => (
                  <tr key={r.id} className="bg-muted/50">
                    <td className="rounded-l-xl px-4 py-3">{r.recipient_email}</td>
                    <td className="px-4 py-3">
                      <EngagementStatusBadge
                        send={{
                          ...r,
                          recipient_id: r.id,
                          campaign_id: campaignId,
                          subject: '',
                          campaign_name: null,
                          source: 'immediate',
                          sent_at: r.sent_at,
                        }}
                      />
                    </td>
                    <td className="rounded-r-xl px-4 py-3">
                      {r.message_id ? (
                        <button
                          type="button"
                          className="text-xs font-mono text-muted-foreground hover:text-primary flex items-center gap-1"
                          onClick={() => copyToClipboard(r.message_id!)}
                          title="Copy email ID"
                        >
                          {r.message_id.slice(0, 12)}…
                          <Copy className="h-3 w-3 shrink-0" />
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <div className="rounded-xl bg-card p-3 text-center">
      <p className={`text-xl font-bold ${className ?? ''}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

export function EmailCampaignHistoryPanel({
  campaigns,
  total,
  loading,
  detail,
  detailLoading,
  selectedId,
  fetchCampaigns,
  fetchCampaignDetail,
  clearDetail,
  recipientLookupEmail,
  recipientSends,
  recipientLookupLoading,
  recipientLookupActive,
  lookupRecipientHistory,
  clearRecipientLookup,
}: EmailCampaignHistoryPanelProps) {
  const [searchEmail, setSearchEmail] = useState('');
  const [recipientFilter, setRecipientFilter] = useState<RecipientEngagementFilter>('all');
  const [engagementOverlay, setEngagementOverlay] = useState<EmailCampaignRecipient[]>([]);

  useEffect(() => {
    setRecipientFilter('all');
    setEngagementOverlay([]);
  }, [selectedId]);

  const handleEngagementLoaded = useCallback((rows: EmailCampaignRecipient[]) => {
    setEngagementOverlay(rows);
  }, []);

  const handleRecipientSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void lookupRecipientHistory(searchEmail);
  };

  if (selectedId && detail) {
    const { campaign, recipients, recipientTotal } = detail;
    const recipientsForFilter = mergeRecipientEngagement(recipients, engagementOverlay);
    const visibleRecipients = recipientsForFilter.filter((row) =>
      recipientMatchesEngagementFilter(row, recipientFilter)
    );
    const filterCounts = recipientEngagementFilterCounts(recipientsForFilter);
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            clearDetail();
            if (recipientLookupActive) {
              void lookupRecipientHistory(recipientLookupEmail);
            }
          }}
          className="gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          {recipientLookupActive ? 'Back to person lookup' : 'Back to history'}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{campaign.campaign_name || campaign.subject}</CardTitle>
            <p className="text-sm text-muted-foreground">{campaign.subject}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">
                {campaign.source === 'scheduled' ? (
                  <span className="flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" />
                    Scheduled
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Send className="h-3 w-3" />
                    Sent immediately
                  </span>
                )}
              </Badge>
              <StatusPill tone="delivered">
                <CheckCircle className="h-3 w-3" />
                {campaign.successful_sends} sent
              </StatusPill>
              {campaign.failed_sends > 0 && (
                <StatusPill tone="bounced">
                  <XCircle className="h-3 w-3" />
                  {campaign.failed_sends} failed
                </StatusPill>
              )}
              <StatusPill tone="opened">
                <Eye className="h-3 w-3" />
                {campaign.open_count ?? 0} opened
              </StatusPill>
              <StatusPill tone="bounced">{campaign.bounce_count ?? 0} bounced</StatusPill>
              <span className="text-sm text-muted-foreground">
                {new Date(campaign.sent_at).toLocaleString()}
              </span>
            </div>

            <CampaignEngagementPanel
              campaignId={campaign.id}
              onEngagementLoaded={handleEngagementLoaded}
            />

            <div>
              <p className="text-sm font-medium text-foreground mb-2">Email content</p>
              <div
                className="rounded-xl bg-muted/50 p-4 max-h-48 overflow-y-auto text-sm prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: campaign.template }}
              />
            </div>

            <div>
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-foreground">
                  Recipients ({recipientFilter === 'all' ? recipientTotal : visibleRecipients.length}
                  {recipientFilter !== 'all' ? ` of ${recipientTotal}` : ''})
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {(
                    [
                      ['all', 'All'],
                      ['opened', 'Opened'],
                      ['bounced', 'Bounced'],
                      ['not_opened', 'Not opened'],
                    ] as Array<[RecipientEngagementFilter, string]>
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      type="button"
                      variant={recipientFilter === value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setRecipientFilter(value)}
                    >
                      {label}
                      <span className="text-xs font-normal opacity-70">{filterCounts[value]}</span>
                    </Button>
                  ))}
                  {bouncedRecipientEmails(recipientsForFilter).length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label="Download bounced"
                      title="Download bounced"
                      onClick={() => {
                        const csv = emailsToCsv(bouncedRecipientEmails(recipientsForFilter));
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'bounced-emails.csv';
                        link.click();
                        URL.revokeObjectURL(url);
                        toast.success('Bounced email list downloaded');
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              {detailLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading recipients...
                </div>
              ) : (
                <div className="table-scroll max-h-80">
                  <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                    <thead>
                      <tr className="text-xs font-medium text-muted-foreground">
                        <th className="px-4 pb-1 text-left">To</th>
                        <th className="px-4 pb-1 text-left">Status</th>
                        <th className="px-4 pb-1 text-left">Subject</th>
                        <th className="px-4 pb-1 text-left">Sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRecipients.length === 0 ? (
                        <tr>
                          <td className="px-4 py-4 text-sm text-muted-foreground" colSpan={4}>
                            {recipientFilter === 'opened'
                              ? 'No one has opened this email yet.'
                              : recipientFilter === 'bounced'
                                ? 'No bounced emails.'
                                : recipientFilter === 'not_opened'
                                  ? 'No unopened emails.'
                                  : 'No recipients found.'}
                          </td>
                        </tr>
                      ) : (
                        visibleRecipients.map((r) => (
                          <tr key={r.id} className="bg-muted/50">
                            <td className="rounded-l-xl px-4 py-3">
                              <div className="flex items-center gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                  <Mail className="h-4 w-4" />
                                </span>
                                <div className="min-w-0">
                                  <button
                                    type="button"
                                    className="truncate text-left font-medium hover:underline"
                                    onClick={() => void lookupRecipientHistory(r.recipient_email)}
                                  >
                                    {r.recipient_email}
                                  </button>
                                  {r.recipient_name ? (
                                    <p className="truncate text-xs text-muted-foreground">
                                      {r.recipient_name}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {r.success ? (
                                <EngagementStatusBadge
                                  send={{
                                    ...r,
                                    recipient_id: r.id,
                                    campaign_id: campaign.id,
                                    subject: '',
                                    campaign_name: null,
                                    source: 'immediate',
                                  }}
                                />
                              ) : (
                                <span
                                  className="text-xs text-destructive"
                                  title={r.error_message || ''}
                                >
                                  Failed{r.error_message ? `: ${r.error_message}` : ''}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{campaign.subject}</td>
                            <td className="rounded-r-xl px-4 py-3 whitespace-nowrap text-muted-foreground">
                              {new Date(r.sent_at).toLocaleString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (recipientLookupActive) {
    return (
      <div className="space-y-4">
        <RecipientSearchForm
          searchEmail={searchEmail}
          setSearchEmail={setSearchEmail}
          onSubmit={handleRecipientSearch}
          loading={recipientLookupLoading}
        />

        <Button variant="outline" size="sm" onClick={clearRecipientLookup} className="gap-2">
          <ChevronLeft className="h-4 w-4" />
          Back to all campaigns
        </Button>

        <div className="space-y-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <User className="h-5 w-5" />
              Emails sent to {recipientLookupEmail}
            </h3>
            <p className="text-sm text-muted-foreground">
              All bulk emails sent to this address, with send status, open tracking, and provider
              email IDs.
            </p>
          </div>
          {recipientLookupLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Looking up...
            </div>
          ) : recipientSends.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No bulk emails found for this address</p>
              <p className="text-sm mt-1">Only emails sent from Bulk Email Service appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {recipientSends.length} email{recipientSends.length !== 1 ? 's' : ''} sent
              </p>
              <div className="table-scroll">
                <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                  <thead>
                    <tr className="text-xs font-medium text-muted-foreground">
                      <th className="px-4 pb-1 text-left">To</th>
                      <th className="px-4 pb-1 text-left">Status</th>
                      <th className="px-4 pb-1 text-left">Subject</th>
                      <th className="px-4 pb-1 text-left">Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipientSends.map((send) => (
                      <tr key={send.recipient_id} className="bg-muted/50">
                        <td className="rounded-l-xl px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                              <Mail className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <p className="truncate font-medium">{recipientLookupEmail}</p>
                              {send.campaign_name ? (
                                <p className="truncate text-xs text-muted-foreground">
                                  {send.campaign_name}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {send.success ? null : (
                              <span
                                className="text-xs text-destructive"
                                title={send.error_message || ''}
                              >
                                Failed
                              </span>
                            )}
                            <EngagementStatusBadge send={send} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{send.subject}</p>
                        </td>
                        <td className="rounded-r-xl px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <span className="whitespace-nowrap text-muted-foreground">
                              {new Date(send.sent_at).toLocaleString()}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => fetchCampaignDetail(send.campaign_id)}
                            >
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <RecipientSearchForm
        searchEmail={searchEmail}
        setSearchEmail={setSearchEmail}
        onSubmit={handleRecipientSearch}
        loading={recipientLookupLoading}
        onRefresh={fetchCampaigns}
        refreshLoading={loading}
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading history...
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No emails sent yet</p>
          <p className="text-sm mt-1">
            History appears after you send or complete a scheduled campaign.
          </p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="min-w-full border-separate border-spacing-y-2 text-sm">
            <thead>
              <tr className="text-xs font-medium text-muted-foreground">
                <th className="px-4 pb-1 text-left">To</th>
                <th className="px-4 pb-1 text-left">Status</th>
                <th className="px-4 pb-1 text-left">Subject</th>
                <th className="px-4 pb-1 text-left">Sent</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const status = campaignListStatus(c);
                return (
                  <tr
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer bg-muted/50 transition-colors hover:bg-muted"
                    onClick={() => fetchCampaignDetail(c.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        fetchCampaignDetail(c.id);
                      }
                    }}
                  >
                    <td className="rounded-l-xl px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Mail className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {c.campaign_name || `${c.total_recipients} recipients`}
                          </p>
                          {c.total_recipients > 1 ? (
                            <span className="mt-1 inline-flex rounded-full bg-background px-2 py-0.5 text-xs text-muted-foreground">
                              +{c.total_recipients - 1}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill tone={LIST_STATUS_TONE[status]}>
                        {LIST_STATUS_LABEL[status]}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3">
                      <p className="truncate text-muted-foreground">{c.subject}</p>
                    </td>
                    <td className="rounded-r-xl px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {new Date(c.sent_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > 0 ? (
        <p className="text-xs text-muted-foreground">
          {total} sent campaign{total !== 1 ? 's' : ''}. Opened and bounced status appears after
          Amazon SES reports them.
        </p>
      ) : null}
    </div>
  );
}

function RecipientSearchForm({
  searchEmail,
  setSearchEmail,
  onSubmit,
  loading,
  onRefresh,
  refreshLoading,
}: {
  searchEmail: string;
  setSearchEmail: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  onRefresh?: () => void;
  refreshLoading?: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="email"
          placeholder="Search..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className="pl-10"
          aria-label="Recipient email to look up"
        />
      </div>
      <Button type="submit" variant="outline" disabled={loading || !searchEmail.trim()}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        Find
      </Button>
      {onRefresh ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onRefresh}
          disabled={refreshLoading}
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshLoading ? 'animate-spin' : ''}`} />
        </Button>
      ) : null}
    </form>
  );
}
